import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { authApi } from '@/api/index';
import ClearIcon from '@/assets/images/icons/clear.svg';
import ExitIcon from '@/assets/images/icons/exit_to_app.svg';
import MenuIcon from '@/assets/images/icons/menu.svg';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import { useClickOutside } from '@/hooks/useClickOutside';
import useCloseByKey from '@/hooks/useCloseByKey';
import useMediaQuery from '@/hooks/useMatchMedia';
import useRoleAccess from '@/hooks/useRoleAccess';
import { fetchCampaignsStat } from '@/store/slices/campaigns/actionCreators';
import { fetchOrgs } from '@/store/slices/orgs/actionCreators';
import { fetchCurrentUserLogo } from '@/store/slices/users/current/actionCreators';
import { removeCurrentUser } from '@/store/slices/users/current/currentUserSlice';
import Button from '@/uikit/Button/Button';
import LangSwitcher from '@/uikit/LangSwitcher/LangSwitcher';
import ThemeSwitcher from '@/uikit/ThemeSwitcher/ThemeSwitcher';
import { deleteCookie, getCookie } from '@/utils/cookie';
import { getDefaultFilterOrgs } from '@/utils/getDefaultFilterOrgs';
import isAccessGranted from '@/utils/isAccessGranted';

import NavigationLink from './NavigationLink';
import routeArray from './routeArray';

import styles from './styles.module.scss';

const NavBar = React.memo(() => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const isMatch1440PX = useMediaQuery('(max-width: 1440px)');
  const isAdmin = useRoleAccess([]);
  const isExportMode = process.env.MARKET === 'export';

  const { user } = useAppSelector((state) => state.currentUserReducer);
  const { logo, isLoading } = useAppSelector((state) => state.currentUserLogoReducer);
  const { counter } = useAppSelector((state) => state.campaignsStatReducer);
  const { orgs } = useAppSelector((state) => state.orgsReducer);

  const [isMenuOpened, setIsMenuOpened] = useState(false);

  const logout = async () => {
    try {
      await authApi.logout({ rt: getCookie('rt') || '' });
    } catch (e) {
      console.warn(e);
    } finally {
      dispatch(removeCurrentUser());
      deleteCookie('jwt');
      deleteCookie('rt');
      navigate('/', { replace: true });
    }
  };

  const wrappedRef = useRef(null);
  useClickOutside(wrappedRef, () => setIsMenuOpened(false));
  useCloseByKey(() => setIsMenuOpened(false));

  useEffect(() => {
    dispatch(fetchCurrentUserLogo());
    dispatch(fetchCampaignsStat());
    // eslint-disable-next-line no-unused-expressions
    !orgs.length && dispatch(fetchOrgs());
  }, []);

  return (
    <div className={styles.navbar} ref={wrappedRef}>
      <div className={`${styles.container} ${isMenuOpened ? styles.opened : ''}`}>
        {!isExportMode && (
          (
          <h1 className={styles.title}>
            {isMatch1440PX && !isMenuOpened
              ? <MenuIcon onClick={() => setIsMenuOpened(!isMenuOpened)} />
              : isMenuOpened
                ? <ClearIcon onClick={() => setIsMenuOpened(!isMenuOpened)} />
                : (
                  isLoading
                    ? <span />
                    : (logo
                      ? <img className={styles.img} src={logo} alt="logo" />
                      : (
                        <>
                          <span>{t('components.NavBar.yandex')}</span>
                          <br />
                          {t('components.NavBar.advert')}
                        </>
                      ))
                )}
          </h1>
          )
        )}
        <ul className={styles.navigation}>
          {user && routeArray.map(({
            route,
            icon,
            lngKey,
            roles,
            noExport,
            additionalRequiredRoles,
          }) => {
            if (!isAccessGranted(roles, user, false, additionalRequiredRoles)) return null;
            if (isExportMode && noExport) return null;
            return (
              <NavigationLink
                key={route}
                route={route.includes('campaigns') && isAdmin
                  ? `campaigns?org=${getDefaultFilterOrgs(orgs)}&campaignStatus=true&orgType=oncar`
                  : route}
                icon={icon}
                isMenuOpened={isMenuOpened}
                stickerText={route.includes('moderation') && isAdmin ? counter : 0}
                onClick={() => setIsMenuOpened(false)}
                lngKey={lngKey}
              />
            );
          })}
        </ul>
        {(!isMatch1440PX || isMenuOpened) && (
          <div className={styles.version}>
            {`${t('common.version')} ${process.env.VERSION}`}
            <br />
            {user && user.email}
          </div>
        )}
        <div className={styles.footer}>
          {isMatch1440PX && !isMenuOpened
            ? <ExitIcon onClick={logout} className={styles.exitIcon} />
            : (
              <>
                <div className={styles.controls}>
                  <LangSwitcher openUp />
                  <ThemeSwitcher />
                </div>
                <Button onClick={logout} fullWidth lngKey="common.exit" />
              </>
            )}
        </div>
      </div>
    </div>
  );
});

export default NavBar;
